import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { hasAccess } from "../rbac/access";
import { mutator } from "./mutator";

export const deleteApiKey = mutator({
  schema: z.object({ id: z.string() }),
  resolve: {},
  permissions: {
    none: "a key is deleted by its owner; a tenant user-manager can revoke a user key, and allow_all admins any key — checked in fn",
  },
  fn: async ({ tx, ctx, args: { id } }) => {
    const { userId } = ctx;

    // The id lookup is unscoped, so match the resolve-layer tenant boundary:
    // without this, the allow_all branch would let a tenant admin delete
    // another tenant's key by id.
    const [key] = await tx.run(zql.apikey.where("id", id));
    if (!key || key.tenantId !== ctx.tenantId)
      throw userFacingError("API key not found");

    // Owners revoke their own key. A tenant user-manager can also revoke a user
    // key (referenceId set, no service account) — e.g. offboarding a member
    // without deactivating them — matching who can see it (canReadApiKey). Other
    // keys (service account / space) still require allow_all.
    if (key.referenceId !== userId) {
      const isUserKey = key.referenceId != null && key.serviceAccountId == null;
      const permitted =
        (isUserKey &&
          // principal-exempt: browser /zero/mutate only; ctx.principal is never
          // a service account here.
          (await hasAccess({
            tx,
            userId,
            scopeId: ctx.tenantId,
            tenantId: ctx.tenantId,
            permission: "tenant:users_manage",
          }))) ||
        // principal-exempt: browser /zero/mutate only; ctx.principal is never a
        // service account here.
        (await hasAccess({
          tx,
          userId,
          scopeId: ctx.tenantId,
          tenantId: ctx.tenantId,
          permission: "allow_all",
        }));
      if (!permitted) throw userFacingError("API key not found");
    }

    await tx.mutate.apikey.delete({ id });
  },
});
